import type { Guide } from "../types";

// ── Screens ──────────────────────────────────────────────────────────────────
// The everyday operator workflows for the Screens area: pairing hardware,
// launching a browser-based virtual screen, putting content on a screen, the
// per-screen "more actions" menu, moving a screen between Spaces, the Wall View
// and its screen panel (volume, brightness, location, device actions), screen
// groups, health, Screen Fit Studio and Wall Studio.

export const screensGuides: Guide[] = [
  {
    slug: "pair-a-screen",
    category: "screens",
    route: "/screens/pair-a-screen",
    title: "Pair a screen",
    summary:
      "Connect a physical player (Android box, TV, or PC) to your Space with its 6-character pairing code or its QR code.",
    intro:
      "Pairing links a display running the PulsePanels player to your account, so you can send content to it from the dashboard. An unpaired player shows a 6-character pairing code (and, on current players, a QR code). You either type the code on the Screens page or scan the QR code with your phone.",
    prerequisites: [
      "The PulsePanels player app is installed and open on the display.",
      "The display shows a pairing code.",
      "You are signed in with a role that can pair screens (for example Admin, Workspace admin or Content manager).",
      "You have a valid licence (without one, the pairing buttons are greyed out) with a free slot for a real device. When real devices fill every slot, the New Screen dialog still opens but only accepts a virtual screen's code.",
    ],
    readingTime: 3,
    status: "draft",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Choose the right Space",
        blocks: [
          {
            type: "p",
            text: "A screen is paired into the Space selected at the top of the sidebar. If you have more than one Space, pick the one this screen belongs to first.",
          },
        ],
      },
      {
        title: "Open the Screens page",
        blocks: [
          {
            type: "p",
            text: "In the left sidebar, click Screens. This lists every screen in the current Space and is where new screens are added.",
          },
        ],
        screenshot: {
          src: "screens/pair-a-screen/01.png",
          alt: "The Screens page with the sidebar Screens item highlighted",
          caption: "The Screens page — your list of all displays.",
          todo: "Screens page, sidebar 'Screens' item highlighted, Pair screen button visible top-right.",
        },
      },
      {
        title: "Click “Pair screen”",
        blocks: [
          {
            type: "p",
            text: "Click Pair screen in the toolbar above the list (the purple Add screen button opens the same dialog). The New Screen dialog opens, ready for the code.",
          },
          {
            type: "note",
            tone: "tip",
            text: "If the Space has no screens yet, the page shows two cards instead — click Pair your screen now.",
          },
        ],
        screenshot: {
          src: "screens/pair-a-screen/02.png",
          alt: "The New Screen dialog with an empty pairing-code field",
          caption: "The New Screen dialog asks for the code shown on the display.",
          todo: "New Screen dialog open with the empty code field — current dialog has no Preview Mode checkbox and no “?” badge after the prompt.",
        },
      },
      {
        title: "Enter the 6-character code",
        blocks: [
          {
            type: "p",
            text: "Type the code shown on the display. Letters are capitalised for you, and Continue lights up once all 6 characters are in. Click Continue.",
          },
          {
            type: "p",
            text: "The dialog closes, “Screen paired successfully.” appears, and the display switches from its pairing screen within a few seconds.",
          },
        ],
        screenshot: {
          src: "screens/pair-a-screen/03.png",
          alt: "The New Screen dialog with a pairing code typed in",
          caption: "Type the on-screen code, then click Continue.",
          todo: "New Screen dialog with the code from the screen (DNJXSD, the same code as the pairing-screen image) typed in and Continue enabled — no Preview Mode checkbox.",
        },
      },
      {
        title: "Or scan the QR code",
        blocks: [
          {
            type: "p",
            text: "If the display shows a QR code next to its pairing code, scan it with your phone camera. Sign in if asked; you land on a Pair this screen page showing the screen code and the account you are signed in as.",
          },
          {
            type: "ol",
            items: [
              "If you have more than one Space, choose it under “Pair this screen to:”.",
              "Click Pair screen.",
              "You see “Screen paired successfully!” — you can close the page; the display updates by itself.",
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "If the page says “Pairing link expired” or “Pairing link not found”, or shows “This pairing QR code is no longer valid. Scan the QR code currently shown on the screen.”, you scanned an old QR code (for example a photo taken earlier) — scan the QR code the display shows right now.",
          },
        ],
        screenshot: {
          src: "screens/pair-a-screen/05.png",
          alt: "A PulsePanels player showing Pair this screen with its 6-character code and a Scan to pair QR code",
          caption: "An unpaired player shows its code and, next to it, a QR code to scan.",
          todo: "The Android player's pairing screen at 1920x1080 (PulsePanels logo, Pair this screen + 6-character code, Scan to pair QR). Captured from the Android 7 player in an emulator pointed at the local demo.",
        },
      },
      {
        title: "Name the screen",
        blocks: [
          {
            type: "p",
            text: "A new screen appears at the top of the list with a default name (“Screen”). Click the name — or choose Rename from its ⋯ menu — type a clear name such as “Lobby Screen” or “Reception TV”, and press Enter.",
          },
          {
            type: "note",
            tone: "info",
            text: "A newly paired screen has no content yet. See “Assign content to a screen”. A player that reports its orientation (for example a portrait panel being paired again) starts in that orientation, and the Screens list shows it straight away — so Set content offers channels and playlists of that orientation.",
          },
        ],
        screenshot: {
          src: "screens/pair-a-screen/04.png",
          alt: "The newly paired screen in the Screens list",
          caption: "The screen now appears in your list.",
          todo: "Screens list showing the newly paired screen, status Live.",
        },
      },
      {
        title: "If the code isn't accepted",
        blocks: [
          {
            type: "p",
            text: "If the New Screen dialog says “No screen is showing the code M7KQ2P. Check that it matches the code on the screen exactly (e.g. M vs N, 0 vs O).”, the code you typed doesn't match any display that is waiting to be paired. It almost always means a typo:",
          },
          {
            type: "ul",
            items: [
              "Compare each character with the display — M and N, U and V, 5 and S, 8 and B are easy to mix up.",
              "Pairing codes never contain the letters O or I or the digits 0 or 1. If you typed one of them, look at the display again — it is another character.",
              "Make sure you're reading the code on the display you want to pair, not one written down earlier.",
            ],
          },
          {
            type: "p",
            text: "A code doesn't run out while the display is online — it stays valid as long as the display is showing it. A display that was offline for a while shows a new code when it reconnects, so always use the code that is on the screen now.",
          },
          {
            type: "note",
            tone: "info",
            text: "Scanned the QR code and saw “This pairing QR code is no longer valid. Scan the QR code currently shown on the screen.”? The QR code was an old one — scan the one the display shows now.",
          },
        ],
      },
    ],
    related: [
      { label: "Launch a virtual screen", to: "/screens/launch-a-virtual-screen" },
      { label: "Assign content to a screen", to: "/screens/assign-content" },
      { label: "Understand screen health", to: "/screens/screen-health" },
    ],
    keywords: ["pair", "pairing", "code", "qr", "qr code", "device", "add screen", "connect", "player", "new screen"],
  },

  {
    slug: "launch-a-virtual-screen",
    category: "screens",
    route: "/screens/launch-a-virtual-screen",
    title: "Launch a virtual screen",
    summary:
      "Turn a browser tab into a screen — no hardware needed. Great for testing content or running signage on any device with a browser.",
    intro:
      "A virtual screen behaves like a paired display but runs in a browser tab instead of on a player box. Use it to preview content, to demo the platform, or to run signage on a laptop or kiosk browser without installing anything. Virtual screens are free: they don't use a slot of your screen licence.",
    prerequisites: [
      "Virtual screens are included in your plan (otherwise the button is greyed out).",
      "You have a valid licence. A full screen quota doesn't matter — only real devices use it.",
    ],
    readingTime: 2,
    status: "draft",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Open the Screens page",
        blocks: [
          {
            type: "p",
            text: "Click Screens in the sidebar. Virtual screens are listed alongside your devices, marked Virtual in the Type column and counted in the Virtual screens tile at the top.",
          },
        ],
        screenshot: {
          src: "screens/launch-a-virtual-screen/01.png",
          alt: "The Screens page showing the Virtual screens counter",
          todo: "Screens page top stats row, 'Virtual screens' tile visible.",
        },
      },
      {
        title: "Click “Launch virtual screen”",
        blocks: [
          {
            type: "p",
            text: "Click Launch virtual screen in the toolbar. A new browser tab opens straight away — there is no dialog to fill in.",
          },
        ],
        screenshot: {
          src: "screens/launch-a-virtual-screen/02.png",
          alt: "The Launch virtual screen button in the Screens toolbar",
          todo: "Screens toolbar with the Launch virtual screen button highlighted.",
        },
      },
      {
        title: "Pair the new tab",
        blocks: [
          {
            type: "p",
            text: "The new tab shows Connect your screen, with a QR code and a PAIR CODE. Pair it like any other screen:",
          },
          {
            type: "ul",
            items: [
              "Scan the QR code with your phone and confirm Pair screen, or",
              "Back in the dashboard, click Pair screen and enter the code shown in the tab.",
            ],
          },
          {
            type: "p",
            text: "Once paired, the tab plays whatever you assign to it. Rename it from the Screens list so you can tell it apart from your hardware.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Keep the tab open (or make it full screen) to run it as a live display. If you close it, open it again from the screen's ⋯ menu → Preview.",
          },
        ],
        screenshot: {
          src: "screens/launch-a-virtual-screen/03.png",
          alt: "A virtual screen tab showing Connect your screen with a QR code and pair code",
          todo: "The new virtual screen tab: 'Connect your screen', QR code in the centre, PAIR CODE card on the right.",
        },
      },
    ],
    related: [
      { label: "Pair a screen", to: "/screens/pair-a-screen" },
      { label: "Assign content to a screen", to: "/screens/assign-content" },
    ],
    keywords: ["virtual", "browser", "no hardware", "preview", "demo", "test screen", "qr"],
  },

  {
    slug: "assign-content",
    category: "screens",
    route: "/screens/assign-content",
    title: "Assign content to a screen",
    summary:
      "Choose what a screen plays: a channel, a playlist, a media file, an app, or a Canvas design.",
    intro:
      "Assigning content is how you decide what plays on a screen. You can assign a multi-zone channel, an ordered playlist, a single media file, a live app (like Weather or a Menu Board) or a Canvas design. The screen updates within seconds.",
    prerequisites: [
      "You have at least one screen (paired or virtual) and it is active.",
      "You have something to show. If not, add it first from Media, Playlists, Channels, Apps or Canvas.",
    ],
    readingTime: 3,
    status: "draft",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Find the screen",
        blocks: [
          {
            type: "p",
            text: "On the Screens page, find the screen. Use Search Screens, or the filters (locations, types, orientations, tags, content) if you have many screens.",
          },
        ],
        screenshot: {
          src: "screens/assign-content/01.png",
          alt: "The Screens list with a screen row",
          todo: "Screens list, one screen row clearly visible.",
        },
      },
      {
        title: "Open “Set content”",
        blocks: [
          {
            type: "p",
            text: "Click the ⋯ button at the end of the screen's row and choose Set content.",
          },
          {
            type: "note",
            tone: "info",
            text: "Set content is greyed out on an inactive screen — activate it first. A screen that is part of a screen group or a video wall shows “Screen group” or “Video wall” in the Assigned content column; its content is managed there.",
          },
        ],
        screenshot: {
          src: "screens/assign-content/02.png",
          alt: "The more-actions menu with Set content highlighted",
          caption: "Set content is in the per-screen ⋯ menu.",
          todo: "⋯ menu open on a screen row with 'Set content' highlighted — current menu also has 'Move to another Space' above Delete.",
        },
      },
      {
        title: "Choose what to show",
        blocks: [
          {
            type: "p",
            text: "In the Set content dialog, pick a type on the left:",
          },
          {
            type: "ul",
            items: [
              "Channels — a multi-zone layout (e.g. main video plus a side ticker). Only published channels can be assigned.",
              "Playlists — an ordered sequence of items that loops.",
              "Media — a single image or video, picked from your folders.",
              "Apps — a live app such as Weather, Clock, or a Menu Board.",
              "Canvas — a design you made in Canvas.",
            ],
          },
          {
            type: "p",
            text: "Channels and playlists are filtered to the screen's orientation (landscape or portrait). Select an item.",
          },
        ],
        screenshot: {
          src: "screens/assign-content/03.png",
          alt: "The Set content dialog with Channels, Playlists, Media, Apps and Canvas",
          caption: "Pick a channel, playlist, media file, app or Canvas design.",
          todo: "Set content dialog open, Channels tab selected, list of published channels.",
        },
      },
      {
        title: "Confirm",
        blocks: [
          {
            type: "p",
            text: "Click Confirm. The Assigned content column shows the new content, and the display switches within seconds.",
          },
          {
            type: "note",
            tone: "info",
            text: "To take content off a screen, open the same ⋯ menu and choose Unassign content.",
          },
        ],
        screenshot: {
          src: "screens/assign-content/04.png",
          alt: "The screen row showing its newly assigned content",
          todo: "Screen row now showing the assigned content name in the Assigned content column.",
        },
      },
    ],
    related: [
      { label: "The more-actions menu", to: "/screens/more-actions" },
      { label: "Manage screens as a group", to: "/screens/screen-groups" },
      { label: "Build a playlist", to: "/content/build-a-playlist" },
      { label: "Add an app", to: "/apps/add-an-app" },
    ],
    keywords: ["assign", "set content", "publish", "playlist", "channel", "app", "media", "canvas"],
  },

  {
    slug: "more-actions",
    category: "screens",
    route: "/screens/more-actions",
    title: "The screen “more actions” menu",
    summary:
      "Everything you can do to one screen from its ⋯ menu — preview, set content, refresh, rename, orientation, tags, move, inactivate and delete.",
    intro:
      "Each row on the Screens page has a more-actions menu (the ⋯ button). It is the quick control panel for one screen. You only see the items your role allows.",
    readingTime: 4,
    status: "draft",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the menu",
        blocks: [
          {
            type: "p",
            text: "On the Screens page, click the ⋯ button at the end of a screen's row.",
          },
        ],
        screenshot: {
          src: "screens/more-actions/01.png",
          alt: "The more-actions menu open on a screen row",
          caption: "The full more-actions menu for one screen.",
          todo: "Capture the ⋯ menu open on a screen row showing every item, including 'Move to another Space'.",
        },
      },
      {
        title: "Know what each action does",
        blocks: [
          {
            type: "ul",
            items: [
              "Preview — for a device, opens a preview window with a Snapshot tab (Take a snapshot captures what the screen shows right now) and a Live content tab that mirrors what it is playing. Works only while the device is online. A virtual screen opens in a new tab.",
              "Set content — assign a channel, playlist, media file, app or Canvas design.",
              "Unassign content — remove the current content.",
              "Refresh — tell the player to reload, useful if a screen looks stuck.",
              "Rename — edit the name in place, then press Enter.",
              "Orientation & scale — opens Display settings: Orientation (Landscape, Landscape (flipped), Portrait, Portrait (flipped)) and Display fit (Auto, Fit, Stretch or Fill).",
              "Tags — add labels to filter screens. Press Enter or a comma to add a tag.",
              "Audience AI — optional audience-sensing settings used by Smart Ads campaigns.",
              "Inactivate / Activate — pause a screen without deleting it, then bring it back.",
              "Move to another Space — move the screen without re-pairing it.",
              "Delete — remove the screen from your account.",
            ],
          },
          {
            type: "note",
            tone: "warning",
            text: "Switching between landscape and portrait unassigns a channel or playlist, because those are built for one orientation. Assign content again afterwards.",
          },
          {
            type: "note",
            tone: "warning",
            text: "Delete cannot be undone — the device must be paired again to come back. To stop a screen for a while, use Inactivate.",
          },
          {
            type: "steps-ref",
            label: "Move a screen to another Space",
            to: "/screens/move-to-another-space",
          },
        ],
        screenshot: {
          src: "screens/more-actions/02.png",
          alt: "Close-up of the more-actions menu items",
          todo: "Close-up of the ⋯ menu — Preview, Set content, Unassign content, Refresh, Rename, Orientation & scale, Tags, Audience AI, Inactivate, Move to another Space, Delete.",
        },
      },
      {
        title: "Volume, brightness, location and device actions",
        blocks: [
          {
            type: "p",
            text: "Volume, brightness, the screen's physical location, its time zone, and device actions such as Reboot Device or Update now are not in this menu. They are in the screen panel of the Wall View.",
          },
          {
            type: "steps-ref",
            label: "Screen details: volume, brightness, location & more",
            to: "/screens/screen-details",
          },
        ],
      },
    ],
    related: [
      { label: "Assign content to a screen", to: "/screens/assign-content" },
      { label: "Screen details: volume, brightness, location & more", to: "/screens/screen-details" },
      { label: "Understand screen health", to: "/screens/screen-health" },
    ],
    keywords: [
      "more actions",
      "menu",
      "preview",
      "snapshot",
      "rename",
      "orientation",
      "portrait",
      "landscape",
      "display fit",
      "tags",
      "refresh",
      "delete",
      "inactivate",
      "move",
    ],
  },

  {
    slug: "move-to-another-space",
    category: "screens",
    route: "/screens/move-to-another-space",
    title: "Move a screen to another Space",
    summary:
      "Move a screen from one Space to another without re-pairing it — nobody needs to visit the screen.",
    intro:
      "Every screen belongs to one Space. If a screen should be managed by another team or branch, move it: the device stays paired, and its name, location, tags, orientation and display settings move with it. Its playback history stays where it happened.",
    prerequisites: [
      "Your organization has at least two Spaces.",
      "You can unpair screens in the current Space (an Admin, the Owner, or that Space's Workspace admin).",
      "You can pair screens in the destination Space.",
    ],
    readingTime: 3,
    status: "draft",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open “Move to another Space”",
        blocks: [
          {
            type: "p",
            text: "On the Screens page, click the screen's ⋯ button and choose Move to another Space. The dialog shows the screen's name.",
          },
        ],
        screenshot: {
          src: "screens/move-to-another-space/01.png",
          alt: "The more-actions menu with Move to another Space highlighted",
          todo: "⋯ menu open on a screen row, 'Move to another Space' highlighted.",
        },
      },
      {
        title: "Choose the destination",
        blocks: [
          {
            type: "p",
            text: "Under From you see the current Space. Under To, choose the Space the screen should move to.",
          },
          {
            type: "note",
            tone: "info",
            text: "If there is no Space to choose, the dialog tells you why: either this is the only Space (create another first), or you are not allowed to pair screens in any other Space.",
          },
        ],
        screenshot: {
          src: "screens/move-to-another-space/02.png",
          alt: "The Move to another Space dialog with From and To",
          todo: "Move to another Space dialog: From = Default workspace, To = Downtown Store (demo Space names), the 'This move clears' list visible.",
        },
      },
      {
        title: "Check what the move clears",
        blocks: [
          {
            type: "p",
            text: "Anything tied to the old Space is listed under “This move clears”, for example:",
          },
          {
            type: "ul",
            items: [
              "Assigned content — the screen goes blank until you assign content from the new Space.",
              "Screen Content Group — the screen leaves its group.",
              "Video wall — the screen leaves the wall; its panel stays in the wall, unassigned.",
              "A running quick-publish override, notification groups, meeting rooms or a Wayfinding kiosk link.",
            ],
          },
          {
            type: "p",
            text: "If nothing is assigned, the dialog says nothing will be cleared.",
          },
        ],
      },
      {
        title: "Move the screen",
        blocks: [
          {
            type: "p",
            text: "Click Move screen. The screen disappears from this Space's list and appears in the destination Space. Switch to that Space in the sidebar and assign its content.",
          },
          {
            type: "note",
            tone: "tip",
            text: "A screen without its own time zone now follows the new Space's time zone.",
          },
        ],
      },
    ],
    related: [
      { label: "The more-actions menu", to: "/screens/more-actions" },
      { label: "Assign content to a screen", to: "/screens/assign-content" },
      { label: "Who sees what: people & Spaces", to: "/account/who-sees-what" },
    ],
    keywords: ["move", "space", "workspace", "transfer", "re-pair", "unpair", "branch"],
  },

  {
    slug: "view-wall",
    category: "screens",
    route: "/screens/view-wall",
    title: "View the Wall (all screens at a glance)",
    summary:
      "See a thumbnail and the health of every screen in the Space on one page, and open any screen's details.",
    intro:
      "The Wall View shows every screen in the current Space as a card with its latest snapshot, its content, location and status. It is the fastest way to check that every display is online and playing the right thing.",
    readingTime: 3,
    status: "draft",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the Wall View",
        blocks: [
          {
            type: "p",
            text: "On the Screens page, click Wall View in the toolbar. (You can also press ⌘K / Ctrl+K and choose Wall view.)",
          },
        ],
        screenshot: {
          src: "screens/view-wall/01.png",
          alt: "The Wall View button on the Screens page",
          todo: "Screens page with the 'Wall View' toolbar button highlighted.",
        },
      },
      {
        title: "Read the cards",
        blocks: [
          {
            type: "p",
            text: "The top tiles count Total Screens, Live, Offline, Warnings and the average CPU of live screens. Each card shows:",
          },
          {
            type: "ul",
            items: [
              "A thumbnail — the screen's last snapshot, not a live stream, so the page stays light even with many screens. A screen that was never snapshotted shows its content name and “No snapshot yet”.",
              "A Live, Warning or Offline badge. Warning means the screen is pending pairing or inactive; offline cards say why (for example “No heartbeat”).",
              "The screen's name, content, location and when it last checked in, plus CPU, memory and disk use.",
            ],
          },
          {
            type: "p",
            text: "Filter with All / Live / Offline / Warning, by orientation or by location, sort by name, last seen or status, and switch between grid and list. Heatmap tints the cards by health.",
          },
        ],
        screenshot: {
          src: "screens/view-wall/02.png",
          alt: "The Wall View with screen cards and the stat tiles",
          caption: "Every screen at a glance — snapshot, content and status per card.",
          todo: "Wall View grid.",
        },
      },
      {
        title: "Open a screen's details",
        blocks: [
          {
            type: "p",
            text: "Click a card to open that screen in the panel on the right, with Overview, Controls, Activity and Settings tabs. On a live screen's selected card, click Live to play its content in the card (this downloads its full media), and Stop to go back to the snapshot.",
          },
          {
            type: "steps-ref",
            label: "Screen details: volume, brightness, location & more",
            to: "/screens/screen-details",
          },
        ],
        screenshot: {
          src: "screens/view-wall/03.png",
          alt: "A screen's details panel opened from a Wall View card",
          todo: "A selected card with the Live button, and the right-hand panel on its Overview tab (Screen overview shows 'Box output', not 'Resolution').",
        },
      },
      {
        title: "Act on several screens at once",
        blocks: [
          {
            type: "p",
            text: "Click Select mode, tick the cards you want (or Select all visible), then click Restart or Push update. Commands only reach screens that are online; offline ones are skipped. Click Done to leave select mode.",
          },
          {
            type: "p",
            text: "Under More you can save the current filters as a view (+ Save current view) and reopen it later.",
          },
        ],
      },
    ],
    related: [
      { label: "Screen details: volume, brightness, location & more", to: "/screens/screen-details" },
      { label: "Understand screen health", to: "/screens/screen-health" },
      { label: "The more-actions menu", to: "/screens/more-actions" },
    ],
    keywords: ["wall", "wall view", "mosaic", "overview", "monitor", "snapshot", "thumbnails", "status", "bulk", "restart"],
  },

  {
    slug: "screen-details",
    category: "screens",
    route: "/screens/screen-details",
    title: "Screen details: volume, brightness, location & more",
    summary:
      "Set a screen's volume and brightness, record where it is, and run device actions like Reboot or Update from its details panel.",
    intro:
      "Each screen has a details panel in the Wall View. It is where you set volume and brightness, give the screen a physical location, set its time zone, read device health, and send commands to the device.",
    prerequisites: ["You can manage screens in this Space."],
    readingTime: 4,
    status: "draft",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Open the screen's panel",
        blocks: [
          {
            type: "p",
            text: "Go to Screens → Wall View and click the screen's card. Its panel opens on the right with four tabs: Overview, Controls, Activity and Settings.",
          },
        ],
        screenshot: {
          src: "screens/screen-details/01.png",
          alt: "The Wall View with a screen's panel open on the Overview tab",
          todo: "Wall View, one card selected, right panel on Overview: Screen overview, Now playing, Device health.",
        },
      },
      {
        title: "Overview: what it is playing and how the device is",
        blocks: [
          {
            type: "ul",
            items: [
              "Screen overview — state, assigned content, paired date, orientation, screen ID and Box output (the resolution the player sends over HDMI).",
              "Now playing — the content, with Manage content. If a change is staged but not on the screen yet, Apply or Cancel it here.",
              "Device health — memory, CPU, disk, uptime, app version, OS, IP address and MAC address.",
            ],
          },
          {
            type: "note",
            tone: "tip",
            text: "To catch staged changes on every screen at once, use Apply changes at the right end of any page's title row (shown if you can assign content to screens). A number on it counts the screens waiting; click it to see each screen's current and new content, then Apply or Discard one, or Apply all. With nothing waiting it says All screens are up to date.",
          },
          {
            type: "p",
            text: "When a newer player version exists, App version shows “Update →” with the version number. Click Update now to install it on the next heartbeat. This works for Android and Windows players; Samsung and LG TV players update through their store or a USB image instead.",
          },
        ],
      },
      {
        title: "Controls: volume and brightness",
        blocks: [
          {
            type: "p",
            text: "Open the Controls tab. Under Device controls, drag Volume (or tick Mute) and drag Brightness, then click Save controls. The screen applies them on its next check-in.",
          },
        ],
        screenshot: {
          src: "screens/screen-details/02.png",
          alt: "The Controls tab with the Volume and Brightness sliders",
          todo: "Controls tab: Device controls card (Volume slider + Mute, Brightness slider, Save controls) and Quick actions card below.",
        },
      },
      {
        title: "Controls: quick actions",
        blocks: [
          {
            type: "ul",
            items: [
              "Proof of Operation — create an “Online & Playing” report for this screen.",
              "Report an Issue — open a support request about this screen.",
              "Open Live Preview — the same preview as ⋯ → Preview.",
              "Device Logs — read what the player has reported.",
              "Clear Cache — delete downloaded files (the screen stays paired) and reload.",
              "Reboot Device — restart the device on its next heartbeat.",
              "Emergency Unlock — drop the kiosk lock so a technician on site can exit to the launcher. Use only for on-site recovery.",
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "Clear Cache and Reboot Device need the screen to be online. Commands sent while it is offline are delivered when it reconnects.",
          },
        ],
      },
      {
        title: "Settings: location, tags and schedule",
        blocks: [
          {
            type: "p",
            text: "Open the Settings tab:",
          },
          {
            type: "ul",
            items: [
              "Display — Set up the display opens Screen Fit Studio for this screen (for LED walls, stretched, transparent or curved panels).",
              "Location — type where the screen physically is, e.g. “Main reception”. It shows in the Screens list and in the location filters.",
              "Tags — add or remove tags.",
              "Schedule — the screen's time zone, a daily restart time, and operating hours (Enable 24/7 playback, or days and time frames).",
            ],
          },
          {
            type: "p",
            text: "Click Save settings.",
          },
        ],
        screenshot: {
          src: "screens/screen-details/03.png",
          alt: "The Settings tab with Location, Tags and Schedule",
          todo: "Settings tab: Display card, Location 'Physical location' field filled with 'Main reception', Tags, Schedule, Save settings.",
        },
      },
      {
        title: "Activity",
        blocks: [
          {
            type: "p",
            text: "The Activity tab lists recent changes to the screen and who made them.",
          },
        ],
      },
    ],
    related: [
      { label: "View the Wall", to: "/screens/view-wall" },
      { label: "Screen Fit Studio", to: "/screens/screen-fit-studio" },
      { label: "Proof of play", to: "/account/proof-of-play" },
      { label: "Screen settings (kiosk PIN)", to: "/account/screen-settings" },
    ],
    keywords: [
      "volume",
      "mute",
      "brightness",
      "location",
      "time zone",
      "restart",
      "reboot",
      "update",
      "clear cache",
      "device logs",
      "emergency unlock",
      "operating hours",
      "controls",
    ],
  },

  {
    slug: "screen-groups",
    category: "screens",
    route: "/screens/screen-groups",
    title: "Manage screens as a group",
    summary:
      "Put many screens in a group, give them the same content with one Apply, schedule weekly time slots or date-range events, and optionally keep their playback in sync.",
    intro:
      "Screen groups let you treat several displays as one — ideal for a row of menu boards or a set of lobby screens that should always match. You set the group's content, then Apply pushes it to every member.",
    prerequisites: [
      "Screen Groups appears in the sidebar only when it is enabled for your account.",
      "A group is landscape or portrait; only screens of that orientation can join (flipped screens included).",
    ],
    readingTime: 5,
    status: "draft",
    lastUpdated: "2026-10-11",
    steps: [
      {
        title: "Open Screen Groups",
        blocks: [
          {
            type: "p",
            text: "Click Screen Groups in the sidebar to see your groups as cards (or a list), with their screens, content and health.",
          },
        ],
        screenshot: {
          src: "screens/screen-groups/01.png",
          alt: "The Screen Groups page",
          todo: "Screen Groups page with stat tiles and one group card.",
        },
      },
      {
        title: "Create a group",
        blocks: [
          {
            type: "p",
            text: "Click New group. Give it a name, choose Landscape or Portrait, and click Create group. The group opens.",
          },
        ],
        screenshot: {
          src: "screens/screen-groups/02.png",
          alt: "The New screen group dialog with Name and Orientation",
          todo: "New screen group dialog with a neutral name typed (e.g. 'Lobby screens') and Orientation Landscape.",
        },
      },
      {
        title: "Add screens",
        blocks: [
          {
            type: "p",
            text: "Under Screens in this group, click Add screens. The list shows every screen of the group's orientation; a screen already in another group is tagged with that group's name. Tick the screens and click Add N screens.",
          },
          {
            type: "note",
            tone: "info",
            text: "A screen in a group takes its content from the group. The Screens page shows “Screen group” in its Assigned content column.",
          },
        ],
      },
      {
        title: "Set the content and apply it",
        blocks: [
          {
            type: "p",
            text: "Under Group content, click Change content and pick the content in the Set content dialog. The change is staged; click the group's Apply changes button, next to Edit group, to push it to every member screen. The button is greyed out while there is nothing new to apply.",
          },
          {
            type: "note",
            tone: "info",
            text: "The Apply changes button at the far right of the title row is a different one: it appears on every page and lists any screens in your account with changes that aren't on them yet — see “Screen details”.",
          },
        ],
        screenshot: {
          src: "screens/screen-groups/03.png",
          alt: "A group's page with Group content, its members, the group's Apply changes button next to Edit group, and the page-wide Apply changes at the far right",
          todo: "A group's detail page — Group content panel with Change content, Screens in this group with members; title row: Delete group, Duplicate, Edit group, the group's Apply changes (greyed while nothing is staged) and the page-wide Apply changes at the far right.",
        },
      },
      {
        title: "Schedule time slots (optional)",
        blocks: [
          {
            type: "p",
            text: "Under Schedule, click Add time slot to play different content at certain times. Outside every slot, members play the group content. Under Runs, choose how the slot repeats:",
          },
          {
            type: "ul",
            items: [
              "Every week: plays on the days you pick (none = every day) between two times, for example a lunch menu from 11:00 to 14:00 on weekdays. A window that ends before it starts runs overnight into the next day.",
              "Date range: plays once, without a break, from a start date and time to an end date and time, for example an event from Monday 5:00 PM to the following Tuesday. Tick No end date to keep it playing until you turn it off or delete it.",
            ],
          },
          {
            type: "p",
            text: "Pick the content (a channel, playlist, media or app) and click Add slot. The screens switch on their own at each start and end, in the timezone shown above the slot list.",
          },
        ],
        screenshot: {
          src: "screens/screen-groups/04.png",
          alt: "The Edit time slot dialog set to Date range: starts Oct 12 at 5:00 PM, ends Oct 20 at 5:00 PM, with the No end date option and the content",
        },
      },
      {
        title: "Run events one after another (optional)",
        blocks: [
          {
            type: "p",
            text: "To run one event and then hand over to the next, add a date-range slot for each. Make the second one start at the moment the first ends: the screens change over at that minute.",
          },
          {
            type: "p",
            text: "Each slot in the list shows its dates and a badge: Scheduled (not started yet), Active (playing now), Ended or Off. Ended slots stay in the list until you delete them.",
          },
          {
            type: "note",
            tone: "info",
            text: "When slots overlap, a date-range slot plays instead of a weekly one. Between two slots of the same kind, the one edited most recently plays.",
          },
          {
            type: "note",
            tone: "tip",
            text: "To schedule a single screen, make a group with just that screen in it.",
          },
        ],
        screenshot: {
          src: "screens/screen-groups/05.png",
          alt: "A group page whose Schedule panel lists a weekly Lunch menu slot, an active Open Day, and Spring Exhibition handing over to Teaching Week on Oct 20 at 17:00",
        },
      },
      {
        title: "Keep playback in sync (optional)",
        blocks: [
          {
            type: "p",
            text: "Click Edit group and tick Sync playback across screens, then Save settings. Every screen then shows the same item at the same moment, timed by the server clock. A screen that finishes downloading later joins at the current point instead of starting over.",
          },
        ],
      },
    ],
    related: [
      { label: "Assign content to a screen", to: "/screens/assign-content" },
      { label: "Wall Studio (video walls)", to: "/screens/wall-studio" },
    ],
    keywords: ["group", "groups", "bulk", "many screens", "sync", "synchronized", "schedule", "together", "apply", "time slot", "date range", "event", "start date", "end date", "no end date"],
  },

  {
    slug: "screen-health",
    category: "screens",
    route: "/screens/screen-health",
    title: "Understand screen health",
    summary:
      "Read a screen's status and health score, see what needs attention, and know what to try when a screen misbehaves.",
    intro:
      "Every screen reports in about once a minute. The Screens page turns that into a status, a health score and an Attention list, so you can spot problems before your audience does.",
    readingTime: 3,
    status: "draft",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Status and health on the Screens page",
        blocks: [
          {
            type: "p",
            text: "The Status column shows one of:",
          },
          {
            type: "ul",
            items: [
              "Live — the screen checked in within the last minute and a half.",
              "Offline — the player has not checked in; check power and network.",
              "Pending — pairing has not finished.",
              "Inactive — the screen was paused with Inactivate.",
            ],
          },
          {
            type: "p",
            text: "The Health column is a score: a live screen scores 90% or more, an offline screen 30%, an inactive screen 0%.",
          },
        ],
        screenshot: {
          src: "screens/screen-health/01.png",
          alt: "Screen rows with the Status and Health columns",
          todo: "Screens list with a Live and an Offline row; Status and Health columns visible.",
        },
      },
      {
        title: "Check what needs attention",
        blocks: [
          {
            type: "p",
            text: "Click the Attention tab above the list, or read the Attention required panel below it. A screen is listed when it is offline, has a low health score, or when two devices are paired as this screen.",
          },
          {
            type: "note",
            tone: "warning",
            text: "“2 devices are paired as this screen” usually means a player was copied from a box that was already paired. Both boxes play the same content and the screen looks healthy, but the second box is invisible. Hover the line to see both devices' MAC addresses, then reset and pair the extra box as its own screen.",
          },
        ],
        screenshot: {
          src: "screens/screen-health/02.png",
          alt: "The Attention required panel on the Screens page",
          caption: "Offline, low-health and doubly-paired screens are listed here.",
          todo: "Capture the Attention required panel at the bottom of the Screens page with at least one entry (ideally a '2 devices are paired as this screen' line).",
        },
      },
      {
        title: "See device health",
        blocks: [
          {
            type: "p",
            text: "In Wall View, open a screen's panel: Device health shows memory, CPU, disk, uptime and the player version, and the Activity tab lists recent events.",
          },
          {
            type: "steps-ref",
            label: "Screen details: volume, brightness, location & more",
            to: "/screens/screen-details",
          },
        ],
      },
      {
        title: "Fix a problem screen",
        blocks: [
          {
            type: "ol",
            items: [
              "Try Refresh from the ⋯ menu to reload the player.",
              "Check that the display has power and a working network connection.",
              "Check that content is assigned (Set content).",
              "In the screen's panel, check Device Logs, then try Clear Cache or Reboot Device.",
              "If it stays offline, restart the player device on site.",
            ],
          },
          {
            type: "steps-ref",
            label: "See the more-actions menu",
            to: "/screens/more-actions",
          },
        ],
      },
    ],
    related: [
      { label: "View the Wall", to: "/screens/view-wall" },
      { label: "Screen details: volume, brightness, location & more", to: "/screens/screen-details" },
      { label: "The more-actions menu", to: "/screens/more-actions" },
    ],
    keywords: ["health", "online", "offline", "status", "attention", "troubleshoot", "not playing", "duplicate", "two devices"],
  },

  {
    slug: "screen-fit-studio",
    category: "screens",
    route: "/screens/screen-fit-studio",
    title: "Screen Fit Studio",
    summary:
      "Describe the real display behind a screen — LED wall, stretched bar, transparent, round, curved or corner — so content fits it, and preview any content on any shape.",
    intro:
      "A player box only reports the picture it sends over HDMI (for example 1920×1080), never the panel behind it. Screen Fit Studio lets you describe the actual display, and PulsePanels sets the canvas and fit. It has two tabs: Display profile (set up one real screen) and Content preview (try content on any shape).",
    prerequisites: ["You can manage screens in this Space."],
    readingTime: 5,
    status: "draft",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open Screen Fit",
        blocks: [
          {
            type: "p",
            text: "Click Screen Fit in the sidebar. The page opens on the Display profile tab. (From a screen's panel in Wall View, Settings → Set up the display opens it with that screen already chosen.)",
          },
        ],
        screenshot: {
          src: "screens/screen-fit-studio/01.png",
          alt: "Screen Fit Studio on the Display profile tab",
          todo: "Screen Fit Studio, Display profile tab, 'Choose a screen' card with the searchable screen list.",
        },
      },
      {
        title: "Choose a screen",
        blocks: [
          {
            type: "p",
            text: "Under Choose a screen, search or pick the screen. Recently used screens appear under Quick select. The Selected screen card shows its Box output, device, player and the settings Applied now.",
          },
        ],
      },
      {
        title: "Pick the display type and size",
        blocks: [
          {
            type: "p",
            text: "Under Display type, choose what the display is: TV / LCD, Stretched bar LCD, LCD video wall, LED wall, LED mesh / curtain, Transparent LED, Transparent LCD/OLED, LED poster, Round display, Curved display, or Corner / wrap-around.",
          },
          {
            type: "p",
            text: "Fill in Size. For LED displays that means cabinets across and down, pixels per cabinet and the pixel pitch; you can also mark positions with no cabinet for an irregular shape. Curved displays ask for the curve, and some types ask whether they are installed on sliding doors.",
          },
          {
            type: "note",
            tone: "info",
            text: "A wall of TVs is set up in Wall Studio instead.",
          },
        ],
        screenshot: {
          src: "screens/screen-fit-studio/02.png",
          alt: "The Display type tiles and the Size card",
          todo: "Display type tiles with 'LED wall' selected and the Size card filled (e.g. 6 × 3 cabinets).",
        },
      },
      {
        title: "Say how the display shows the box's picture",
        blocks: [
          {
            type: "p",
            text: "Under “How does the display show the box's picture?”, choose one:",
          },
          {
            type: "ul",
            items: [
              "Box outputs the panel's own resolution — 1:1, the whole picture is the panel.",
              "Display scales the whole picture — an LED video processor, or a TV set to Full / Stretch.",
              "Display shows a fixed part, 1:1 — typical of LED sending cards; enter the start X / Y of the lit area.",
              "Display keeps it 16:9 with bars.",
              "I don't know — PulsePanels support will find out.",
            ],
          },
          {
            type: "p",
            text: "Optionally fill in Where is it? (indoor, behind a window, outdoor) and notes for support.",
          },
        ],
      },
      {
        title: "Check the preview",
        blocks: [
          {
            type: "p",
            text: "The Preview on the right shows the screen's assigned content (or a sample image) at the canvas and fit that will be applied, with safe zones, the smallest readable text size and any warnings. Full screen preview makes it larger.",
          },
        ],
        screenshot: {
          src: "screens/screen-fit-studio/03.png",
          alt: "The Preview card showing content on the described display",
          todo: "Right-rail Preview card with Assigned content selected, canvas facts and warnings below.",
        },
      },
      {
        title: "Save the display profile",
        blocks: [
          {
            type: "p",
            text: "Click Save display profile. If the settings change how the screen shows content, confirm with Save and apply — the screen updates right away. The profile never changes the screen's orientation; it only warns you if it doesn't match.",
          },
          {
            type: "p",
            text: "If some details are unknown, or you tick “Ask PulsePanels support to check this setup”, you can add up to 5 photos of the display, controller label or settings, and the button becomes Save and send to support. A support ticket is opened for the screen.",
          },
        ],
      },
      {
        title: "Check it on the real display",
        blocks: [
          {
            type: "p",
            text: "Under Check it on the real display, show a calibration pattern on the screen for 5 minutes:",
          },
          {
            type: "ul",
            items: [
              "Show canvas pattern — the circle must look round and the four corner markers must touch the display's edges.",
              "Show box output grid — the raw HDMI picture with pixel coordinates, to read an LED's start X / Y.",
              "Hide pattern — switch it off early.",
            ],
          },
        ],
      },
      {
        title: "Preview content on any shape",
        blocks: [
          {
            type: "p",
            text: "Switch to the Content preview tab to experiment without touching a screen:",
          },
          {
            type: "ol",
            items: [
              "Under “What does your screen look like?”, pick a display type and a size.",
              "Under Preview with, choose Sample image, Sample app, Screen content (what a chosen screen has assigned) or Your content.",
              "Choose a Fit mode: Smart fit, Fill (crop), Fit (letterbox) or Stretch.",
              "Read Recommended for best viewing for the ideal design size and safe area.",
              "To use this shape on a screen, click Apply to screen and choose the screen.",
            ],
          },
        ],
        screenshot: {
          src: "screens/screen-fit-studio/04.png",
          alt: "The Content preview tab",
          todo: "Content preview tab: display type tiles, 'Preview with' segmented control, Fit mode, Preview card with Apply to screen.",
        },
      },
    ],
    related: [
      { label: "Wall Studio (video walls)", to: "/screens/wall-studio" },
      { label: "Screen details: volume, brightness, location & more", to: "/screens/screen-details" },
      { label: "Create a channel", to: "/content/create-a-channel" },
    ],
    keywords: [
      "screen fit",
      "display profile",
      "led",
      "led wall",
      "stretched",
      "bar",
      "transparent",
      "round",
      "curved",
      "corner",
      "canvas",
      "resolution",
      "test pattern",
      "fit",
      "crop",
      "letterbox",
    ],
  },

  {
    slug: "wall-studio",
    category: "screens",
    route: "/screens/wall-studio",
    title: "Wall Studio (video walls)",
    summary:
      "Join several screens into one video wall, choose content that spans all of them, and apply it — each screen shows its own slice.",
    intro:
      "Wall Studio turns a grid of TVs (or a free arrangement of screens) into one big canvas. You describe the layout, pick the content, assign a screen to each panel, and Apply. Each screen then plays its part of the picture.",
    prerequisites: [
      "The screens are paired and in the current Space.",
      "For a grid wall, all panels should have the same orientation.",
    ],
    readingTime: 5,
    status: "draft",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open Wall Studio",
        blocks: [
          {
            type: "p",
            text: "Click Wall Studio in the sidebar. The page is laid out in five numbered cards. To edit a wall you saved before, pick it from the saved-walls list in card 5, Assign to screens.",
          },
        ],
        screenshot: {
          src: "screens/wall-studio/01.png",
          alt: "Wall Studio with its numbered cards",
          todo: "Wall Studio page: Layout and Screen settings on the left, Preview & arrange and Wall content on the right, Assign to screens below.",
        },
      },
      {
        title: "1 · Layout",
        blocks: [
          {
            type: "p",
            text: "Choose Grid wall or Free layout.",
          },
          {
            type: "ul",
            items: [
              "Grid wall — choose the Panel orientation, then a preset (2 × 2, 3 × 2, 3 × 3, 4 × 2) or set Columns and Rows.",
              "Free layout — choose the canvas orientation, then add Landscape or Portrait panels and drag them into place: drag to move, corners to resize, the top handle to rotate.",
            ],
          },
          {
            type: "p",
            text: "If you have saved templates, Start from a template applies a saved layout and settings.",
          },
        ],
      },
      {
        title: "2 · Screen settings",
        blocks: [
          {
            type: "p",
            text: "For a grid wall, choose each panel's resolution (1080p or 4K). Then choose Content fit:",
          },
          {
            type: "ul",
            items: [
              "Crop (fill) — fills the whole wall; edges are cropped where the shape differs.",
              "Fit (whole) — shows the whole picture, with bars where the shape differs.",
              "Stretch — fills the whole wall with nothing cropped; the picture is squeezed or widened.",
            ],
          },
        ],
      },
      {
        title: "3 · Preview & arrange",
        blocks: [
          {
            type: "p",
            text: "The preview shows the content split across the panels. Turn Show panel numbers on or off, zoom, or go full screen. Set the seams to match your hardware: Seamless / LED, Adjust seams (widen the seam until it matches the gap between your panels), or Panel model (the seam comes from the model's bezel).",
          },
        ],
        screenshot: {
          src: "screens/wall-studio/02.png",
          alt: "The Preview & arrange card with a 2 × 2 wall",
          todo: "Preview & arrange card: 2 × 2 grid with panel numbers, seam buttons and zoom in the toolbar.",
        },
      },
      {
        title: "4 · Wall content",
        blocks: [
          {
            type: "p",
            text: "Pick what plays across the wall: Media, Playlist, Channel or App. Sample content offers a Test pattern (to check alignment and seams) and a Sample poster, for the preview only.",
          },
        ],
      },
      {
        title: "5 · Assign to screens",
        blocks: [
          {
            type: "p",
            text: "Type a Wall name (for example “Lobby 2×2 wall”). Then, for each panel (R1-C1, R1-C2… or Panel 1, Panel 2…), choose the screen that shows it. A screen can only be used once.",
          },
          {
            type: "note",
            tone: "warning",
            text: "If an assigned screen has the other orientation, a warning appears — set the panel orientation to match, or that screen will crop differently than the preview.",
          },
        ],
        screenshot: {
          src: "screens/wall-studio/03.png",
          alt: "The Assign to screens card with a wall name and a screen per panel",
          todo: "Assign to screens card: Wall name filled, each R1-C1… row with a screen selected, saved-walls picker top-right.",
        },
      },
      {
        title: "Apply the wall",
        blocks: [
          {
            type: "p",
            text: "Click Apply to N screens at the top. Apply sends the content and each panel's slice to its screen, then publishes. The Screens page then shows “Video wall” in those screens' Assigned content column.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Screens that are in a screen group for synchronized playback stay in their group when you apply a wall, so the panels keep playing in sync.",
          },
        ],
      },
      {
        title: "Save the layout as a template (optional)",
        blocks: [
          {
            type: "p",
            text: "Click Save as template, give it a Template name and click Save template. Templates keep the layout and settings — not the screens or content — so you can start the next wall from them. Your templates are listed in the same dialog, where you can also delete them.",
          },
        ],
      },
    ],
    related: [
      { label: "Screen Fit Studio", to: "/screens/screen-fit-studio" },
      { label: "Manage screens as a group", to: "/screens/screen-groups" },
      { label: "Assign content to a screen", to: "/screens/assign-content" },
    ],
    keywords: [
      "video wall",
      "wall studio",
      "tv wall",
      "grid",
      "free layout",
      "panels",
      "bezel",
      "seams",
      "template",
      "span",
      "stretch",
      "crop",
    ],
  },
];
